/**
 * actionsApi — thin wrappers over the Action Center endpoints landed in
 * PR 2A (feat/action-center-backend).
 *
 * Two read endpoints:
 *   - GET  /actions                          → items + counts + generated_at
 *   - POST /actions/next-best-action         → ranked action_ids
 *
 * Mutations are NOT bundled — they route through existing per-entity
 * endpoints (follow-up, task, alert). `executeAction` is the single
 * server-side dispatcher, keyed by the item's declared `action_key`
 * (never by action_id string parsing).
 */
import { api } from "@/lib/api";

/**
 * List Action Center items. Server always returns global counts (they
 * do NOT respect the query filters — see PR 2A contract). Client-side
 * filters keep the counts stable across chip/tab toggles.
 */
export async function listActions(params = {}) {
  const { data } = await api.get("/actions", { params });
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    counts: data?.counts || {
      critical: 0,
      today: 0,
      overdue: 0,
      upcoming: 0,
      info: 0,
      total: 0,
    },
    generated_at: data?.generated_at || null,
  };
}

/** Sidebar badge — items=[] with populated counts. Very cheap round-trip. */
export async function fetchActionCounts() {
  const { data } = await api.get("/actions", { params: { counts_only: true } });
  return data?.counts || {
    critical: 0,
    today: 0,
    overdue: 0,
    upcoming: 0,
    info: 0,
    total: 0,
  };
}

/**
 * NBA v2 — the LLM sees only projected action items and returns
 * `action_id`s already validated server-side against the current
 * /actions payload. The frontend uses those ids to render the top-3.
 */
export async function nextBestAction({ lang = "es" } = {}) {
  const { data } = await api.post(
    "/actions/next-best-action",
    null,
    { params: { lang } },
  );
  return {
    source: data?.source || "empty",
    greeting: data?.greeting || "",
    actions: Array.isArray(data?.actions) ? data.actions : [],
  };
}

/**
 * Dispatcher — the ONLY way ActionCenter mutates state. Never inspects
 * `action_id` (which is a display identity), only `actionKey` + the
 * fields the item already exposes (`entity_id`, `meta.*`).
 *
 * Every branch awaits the backend 2xx before returning. Callers must
 * reload /actions on success — no optimistic UI in this PR (the
 * ActionCenter card stays put until the reload lands).
 */
export async function executeAction(item, actionKey, payload = {}) {
  if (!item || !actionKey) {
    throw new Error("executeAction: item + actionKey required");
  }
  const meta = item.meta || {};

  switch (actionKey) {
    // -------- Follow-up mutations --------
    case "followup_complete": {
      const leadId = item.entity_id;
      const { data } = await api.post(
        `/leads/${encodeURIComponent(leadId)}/next-action/complete`,
      );
      return data;
    }
    case "followup_snooze": {
      const leadId = item.entity_id;
      if (!payload.until) throw new Error("followup_snooze needs {until: ISO}");
      const { data } = await api.post(
        `/leads/${encodeURIComponent(leadId)}/next-action/snooze`,
        { until: payload.until },
      );
      return data;
    }
    case "followup_dismiss": {
      const leadId = item.entity_id;
      const { data } = await api.post(
        `/leads/${encodeURIComponent(leadId)}/next-action/dismiss`,
      );
      return data;
    }

    // -------- Virtual reply card: acknowledge the CURRENT inbound --------
    case "reply_acknowledge": {
      // "Marcar como atendido" — purpose-built for the virtual
      // "Responder a …" card. meta.inbound_at is the inbound the operator
      // saw when the card was built; the server anchors the ack to it so
      // an inbound that landed meanwhile keeps the card visible. Never
      // touches the hidden manual follow-up (no complete/snooze/dismiss).
      const leadId = item.entity_id;
      const body = meta.inbound_at ? { inbound_at: meta.inbound_at } : {};
      const { data } = await api.post(
        `/leads/${encodeURIComponent(leadId)}/reply/acknowledge`,
        body,
      );
      return data;
    }

    // -------- Task mutation --------
    case "task_complete": {
      // Prefer meta.task_id so the source of truth is explicit; fall back
      // to entity_id for adapters that don't set meta yet.
      const taskId = meta.task_id || item.entity_id;
      const { data } = await api.patch(
        `/tasks/${encodeURIComponent(taskId)}`,
        { status: "done" },
      );
      return data;
    }

    // -------- Alert dismiss / snooze --------
    case "alert_dismiss": {
      const alertId = meta.alert_id;
      if (!alertId) throw new Error("alert_dismiss needs meta.alert_id");
      const { data } = await api.post(
        `/alerts/${encodeURIComponent(alertId)}/dismiss`,
      );
      return data;
    }
    case "alert_snooze": {
      const alertId = meta.alert_id;
      if (!alertId) throw new Error("alert_snooze needs meta.alert_id");
      const days = Number.isFinite(payload.days) ? payload.days : 7;
      const { data } = await api.post(
        `/alerts/${encodeURIComponent(alertId)}/dismiss`,
        { snooze_days: days },
      );
      return data;
    }

    default:
      throw new Error(`executeAction: unknown actionKey "${actionKey}"`);
  }
}
